/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1744848000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Apr 17 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-sheets-source",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Google Sheets formatting requirements",
          "depth": 0,
          "id": "google-sheets-formatting-requirements"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Authorize connection",
          "depth": 1,
          "id": "authorize-connection"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Internal error encountered",
          "depth": 1,
          "id": "internal-error-encountered"
        }, {
          "value": "Unable to parse range",
          "depth": 1,
          "id": "unable-to-parse-range"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      a: "a",
      strong: "strong",
      img: "img",
      h3: "h3",
      ol: "ol"
    }, _provideComponents(), props.components), {CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in Google Sheets and push it to downstream destinations. Before connecting your Google Sheets, it's best to make sure they meet formatting requirements."
      }), "\n", _jsx(_components.h2, {
        id: "google-sheets-formatting-requirements",
        children: "Google Sheets formatting requirements"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Your Google Sheet must be a Sheets file, not an ", _jsx(_components.code, {
            children: ".XLS"
          }), " or ", _jsx(_components.code, {
            children: ".XLSX"
          }), " file. If this is the case, you need to convert it to a Sheets file. To do so, go to ", _jsx(_components.a, {
            href: "https://support.google.com/docs/answer/9331167?hl=en#2.5",
            children: _jsx(_components.strong, {
              children: "File > Save as Google Sheets"
            })
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Google Sheet must have column names in the first row of your sheet. In the following image, you can see that the column names are ", _jsx(_components.code, {
            children: "id"
          }), ", ", _jsx(_components.code, {
            children: "created_at"
          }), ", ", _jsx(_components.code, {
            children: "name"
          }), ", ", _jsx(_components.code, {
            children: "website"
          }), ", and ", _jsx(_components.code, {
            children: "description"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-google-sheet-example.png",
          alt: "Google Sheet Example"
        })
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Google Sheets"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "authorize-connection",
        children: "Authorize connection"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Log in to Google Sheets"
          }), " and select the desired Google Account."]
        }), "\n", _jsxs(_components.li, {
          children: ["After reading what authorization you're giving to Hightouch, select ", _jsx(_components.strong, {
            children: "Allow"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Spreadsheet"
          }), " containg the data you wish to sync from the dropdown."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Remember that ", _jsxs(_components.a, {
            href: "#google-sheets-formatting-requirements",
            children: [_jsx(_components.code, {
              children: ".XLS"
            }), " or ", _jsx(_components.code, {
              children: ".XLSX"
            }), " file types aren't supported"]
          }), " and won't appear in the dropdown."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Google Sheets as a source for the first time, Hightouch checks that you have the correct permissions. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Google Sheets."]
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add model."
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select the Google Sheets source you previously created."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Sheet name"
          }), " of the data you want to sync. Hightouch treats each sheet as a separate table. If you want to sync data from multiple sheets, you need to set up a separate model for each one."]
        }), "\n", _jsx(_components.li, {
          children: "Preview your model's query results."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name your model and select its ", _jsx(_components.a, {
            href: "/models/creating-models#unique-primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary key"
            })
          }), ". Hightouch uses the primary key to determine which rows have been added, changed, or removed since the last sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "internal-error-encountered",
        children: "Internal error encountered"
      }), "\n", _jsxs(_components.p, {
        children: ["This error may occur when the spreadsheet you're syncing to contains sheets with charts or pivot tables.\nTry moving the data you would like to update with Hightouch to a separate spreadsheet.\nThen reference this data's range using a formula like ", _jsx(_components.a, {
          href: "https://support.google.com/docs/answer/3093340?hl=en",
          children: _jsx(_components.code, {
            children: "IMPORTRANGE"
          })
        }), " in the spreadsheet with your charts and analysis.\nThis way, the sheet that Hightouch updates won't have the timeout issue, and you can still see the data on the sheet that contains charts or pivot tables."]
      }), "\n", _jsx(_components.h3, {
        id: "unable-to-parse-range",
        children: "Unable to parse range"
      }), "\n", _jsx(_components.p, {
        children: "This error indicates that the spreadsheet (in the source configuration) or the sheet (in the model configuration) have either been renamed or deleted. You can fix this by ensuring that both the source and model are querying data from an existing spreadsheet and sheet."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
